
import React, { useEffect, useState, useRef, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { fetchPolicies, fetchPoliciesForMultipleUsers } from "../policies/policySlice";
import { useNavigate, useLocation } from "react-router-dom";
import { FiSearch } from "react-icons/fi";
import Box from "@mui/material/Box";
import useMediaQuery from "@mui/material/useMediaQuery";
import Typography from "@mui/material/Typography";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import TextField from "@mui/material/TextField";
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import PersonIcon from '@mui/icons-material/Person';
import CategoryIcon from '@mui/icons-material/Category';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import DescriptionIcon from '@mui/icons-material/Description';
import BusinessIcon from '@mui/icons-material/Business';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import Popover from '@mui/material/Popover';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Badge from '@mui/material/Badge';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Alert from '@mui/material/Alert';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import LinearProgress from '@mui/material/LinearProgress';
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CloseIcon from '@mui/icons-material/Close';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import InputLabel from '@mui/material/InputLabel';
import { getAuth } from 'firebase/auth';
import { COMPANIES, POLICY_TYPES } from "../../data/dropdowns";
import Pagination from "@mui/material/Pagination";
import { shouldUseMobileLayout } from "../../utils/deviceDetection";
import { getPolicyCountsByCategory } from "../../utils/countUtils";
import { calculateCommission } from "../../CommissionInfo/commissionLogic";
import { useAdminStaffData } from "../../hooks/useAdminStaffData";
import { firebaseCache } from "../../utils/firebaseCache";
import PaidIcon from '@mui/icons-material/Paid';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";
import MobileCommissionList from "./MobileCommissionList";
import dayjs from "dayjs";
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';

const CommissionList = () => {
    // Helper to calculate commission dynamically
    const calculatePolicyCommission = (policy) => {
        // If totalCommission is already pre-calculated and stored, use it
        return Math.max(0, parseFloat(policy.totalCommission || 0));
    };

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const { policies, loading } = useSelector((state) => state.policies);
    const { user } = useSelector((state) => state.auth);
    const { userIds, isAdmin, loading: staffLoading } = useAdminStaffData();

    const [searchTerm, setSearchTerm] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedCategory, setSelectedCategory] = useState("motor");

    // Filter state
    const [filterAnchorEl, setFilterAnchorEl] = useState(null);
    const [selectedCompanies, setSelectedCompanies] = useState([]);
    const [selectedPolicyTypes, setSelectedPolicyTypes] = useState({});
    const [showCompanyOptions, setShowCompanyOptions] = useState(false);
    const [showPolicyTypeOptions, setShowPolicyTypeOptions] = useState(false);
    const [dateRange, setDateRange] = useState({ start: null, end: null });
    const [showDateRangeCalendar, setShowDateRangeCalendar] = useState(null);
    const calendarRef = useRef();
    const isInitialMount = useRef(true);

    // Upload dialog state
    const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
    const [uploadFile, setUploadFile] = useState(null);
    const [uploadLoading, setUploadLoading] = useState(false);
    const [uploadResult, setUploadResult] = useState(null);
    const [uploadError, setUploadError] = useState(null);
    const [uploadCompany, setUploadCompany] = useState('');
    const [isDragging, setIsDragging] = useState(false);
    const fileInputRef = useRef(null);

    const isTinyScreen = useMediaQuery('(max-height:720px)');
    const isShortScreen = useMediaQuery('(max-height:900px)');
    const isMediumScreen = useMediaQuery('(max-height:1080px)');
    const itemsPerPage = isTinyScreen ? 4 : isShortScreen ? 5 : isMediumScreen ? 6 : 8;
    const categories = ["motor", "non-motor", "life"];

    const isMobile = shouldUseMobileLayout();
    const isSmallScreen = useMediaQuery('(max-width:1281px)');

    if (isMobile) {
        return <MobileCommissionList />;
    }

    // Fetch Logic
    useEffect(() => {
        // Wait for useAdminStaffData to resolve admin status and staff UIDs
        if (staffLoading) return;

        if (isAdmin && userIds.length > 0) {
            console.log('Admin user - fetching aggregated policies for commissions:', userIds);
            dispatch(fetchPoliciesForMultipleUsers(userIds));
        } else if (user?.uid && !isAdmin) {
            console.log('Staff user - fetching own policies for commissions');
            dispatch(fetchPolicies(user.uid));
        }
    }, [userIds, isAdmin, user?.uid, staffLoading, dispatch]);

    // Restore filter state from sessionStorage
    useEffect(() => {
        const savedFilters = sessionStorage.getItem('commissionList_filterState');
        if (savedFilters) {
            try {
                const filters = JSON.parse(savedFilters);
                setSelectedCompanies(filters.selectedCompanies || []);
                setSelectedPolicyTypes(filters.selectedPolicyTypes || {});
                setShowCompanyOptions(filters.showCompanyOptions || false);
                setShowPolicyTypeOptions(filters.showPolicyTypeOptions || false);
                if (filters.dateRange) {
                    setDateRange({
                        start: filters.dateRange.start ? dayjs(filters.dateRange.start) : null,
                        end: filters.dateRange.end ? dayjs(filters.dateRange.end) : null
                    });
                }
            } catch (error) {
                console.error('Error restoring filter state:', error);
            }
        }
        setTimeout(() => { isInitialMount.current = false; }, 100);
    }, []);

    // Save filter state to sessionStorage
    useEffect(() => {
        if (isInitialMount.current) return;
        const filterState = {
            selectedCompanies,
            selectedPolicyTypes,
            showCompanyOptions,
            showPolicyTypeOptions,
            dateRange: {
                start: dateRange.start ? dateRange.start.toISOString() : null,
                end: dateRange.end ? dateRange.end.toISOString() : null
            }
        };
        sessionStorage.setItem('commissionList_filterState', JSON.stringify(filterState));
    }, [selectedCompanies, selectedPolicyTypes, showCompanyOptions, showPolicyTypeOptions, dateRange]);

    // Handle navigation state
    useEffect(() => {
        if (location.state?.currentPage) setCurrentPage(location.state.currentPage);
        if (location.state?.fromCategory) setSelectedCategory(location.state.fromCategory);
    }, [location.state]);

    // Filter handlers
    const handleFilterButtonClick = (event) => setFilterAnchorEl(event.currentTarget);
    const handleFilterClose = () => setFilterAnchorEl(null);
    const handleCompanyToggle = (company) => {
        setSelectedCompanies((prev) =>
            prev.includes(company) ? prev.filter((c) => c !== company) : [...prev, company]
        );
    };
    const handlePolicyTypeToggle = (policyType) => {
        setSelectedPolicyTypes((prev) => {
            const currentTabTypes = prev[selectedCategory] || [];
            const updated = currentTabTypes.includes(policyType)
                ? currentTabTypes.filter((t) => t !== policyType)
                : [...currentTabTypes, policyType];
            return { ...prev, [selectedCategory]: updated };
        });
    };
    const handleCompanyCheckboxChange = (checked) => {
        setShowCompanyOptions(checked);
        if (!checked) setSelectedCompanies([]);
    };
    const handlePolicyTypeCheckboxChange = (checked) => {
        setShowPolicyTypeOptions(checked);
        if (!checked) setSelectedPolicyTypes((prev) => ({ ...prev, [selectedCategory]: [] }));
    };
    const clearFilters = () => {
        setSelectedCompanies([]);
        setSelectedPolicyTypes({});
        setShowCompanyOptions(false);
        setShowPolicyTypeOptions(false);
        setDateRange({ start: null, end: null });
    };
    const handleDateRangeCalendarChange = (date) => {
        const selectedDay = dayjs(date);
        if (showDateRangeCalendar === 'start') {
            setDateRange((prev) => ({
                start: selectedDay,
                end: prev.end && selectedDay.isAfter(prev.end) ? null : prev.end
            }));
            setShowDateRangeCalendar('end');
        } else if (showDateRangeCalendar === 'end') {
            if (selectedDay.isBefore(dateRange.start, 'day')) return;
            setDateRange((prev) => ({ start: prev.start, end: selectedDay }));
            setShowDateRangeCalendar(null);
        }
    };

    // Upload handlers
    const handleUploadClick = () => {
        setUploadDialogOpen(true);
        setUploadFile(null);
        setUploadResult(null);
        setUploadError(null);
        setUploadCompany('');
    };

    const handleUploadClose = () => {
        if (uploadResult?.success) {
            console.log("🔄 Commission upload successful, refreshing data...");
            // Invalidate cache (both list and individual policy caches)
            firebaseCache.markForInvalidation('policy');

            // Refresh data (force server fetch)
            if (isAdmin && userIds.length > 0) {
                dispatch(fetchPoliciesForMultipleUsers({ userIds, forceRefresh: true }));
            } else if (user?.uid && !isAdmin) {
                dispatch(fetchPolicies({ agentId: user.uid, forceRefresh: true }));
            }
        }
        setUploadDialogOpen(false);
        setUploadFile(null);
        setUploadResult(null);
        setUploadError(null);
        setUploadCompany('');
    };

    const handleFileSelect = (event) => {
        const file = event.target.files?.[0];
        processSelectedFile(file);
    };

    const processSelectedFile = (file) => {
        if (file) {
            const ext = file.name.split('.').pop().toLowerCase();
            const allowedPdf = ['pdf'];
            const allowedExcel = ['xlsx', 'xls'];

            if (uploadCompany === 'GODIGIT') {
                if (allowedExcel.includes(ext)) {
                    setUploadFile(file);
                    setUploadError(null);
                } else {
                    setUploadError('Please select an Excel file (.xlsx or .xls)');
                }
            } else {
                if (allowedPdf.includes(ext)) {
                    setUploadFile(file);
                    setUploadError(null);
                } else {
                    setUploadError('Please select a PDF file');
                }
            }
        }
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);

        const files = e.dataTransfer.files;
        if (files && files.length > 0) {
            processSelectedFile(files[0]);
        }
    };

    const handleUploadSubmit = async () => {
        if (!uploadFile) return;

        setUploadLoading(true);
        setUploadError(null);
        setUploadResult(null);

        try {
            const auth = getAuth();
            const token = await auth.currentUser?.getIdToken();

            if (!token) {
                throw new Error('Not authenticated');
            }

            if (!uploadCompany) {
                throw new Error('Please select a company');
            }

            const formData = new FormData();
            formData.append('pdf', uploadFile);
            formData.append('company', uploadCompany);

            const backendUrl = import.meta.env.VITE_BACKEND_URL || 'http://localhost:5001';
            const response = await fetch(`${backendUrl}/api/commission/upload`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`
                },
                body: formData
            });

            const result = await response.json();

            if (!response.ok) {
                throw new Error(result.message || result.error || 'Upload failed');
            }

            setUploadResult(result);

            // Refresh policies to show updated commissions (force server fetch)
            if (result.successfullyUpdated > 0) {
                firebaseCache.markForInvalidation('policy');
                if (isAdmin && userIds.length > 0) {
                    dispatch(fetchPoliciesForMultipleUsers({ userIds, forceRefresh: true }));
                } else if (user?.uid) {
                    dispatch(fetchPolicies({ agentId: user.uid, forceRefresh: true }));
                }
            }
        } catch (error) {
            console.error('Upload error:', error);
            setUploadError(error.message || 'Failed to upload commission PDF');
        } finally {
            setUploadLoading(false);
        }
    };

    const activeFilterCount = selectedCompanies.length + ((selectedPolicyTypes[selectedCategory] || []).length) + (dateRange.start && dateRange.end ? 1 : 0);

    // Filtering with all filters
    const filtered = policies.filter((p) => {
        if (p.status === "New Lead") return false;

        const categoryMatch =
            selectedCategory === "motor" ? p.policyCategory === "motor"
                : selectedCategory === "non-motor" ? p.policyCategory === "non-motor"
                    : p.policyCategoryType === selectedCategory;
        if (!categoryMatch) return false;

        // Company filter
        if (selectedCompanies.length > 0 && !selectedCompanies.includes(p.policyCompany)) return false;

        // Policy type filter
        const currentTabPolicyTypes = selectedPolicyTypes[selectedCategory] || [];
        if (currentTabPolicyTypes.length > 0 && !currentTabPolicyTypes.includes(p.policyType)) return false;

        // Date range filter
        if (dateRange.start && dateRange.end) {
            const created = p.createdAt ? (p.createdAt.seconds ? dayjs(p.createdAt.seconds * 1000) : dayjs(p.createdAt)) : null;
            if (!created || !created.isValid()) return false;
            const start = dateRange.start.startOf('day');
            const end = dateRange.end.endOf('day');
            if (created.isBefore(start) || created.isAfter(end)) return false;
        }

        const searchLower = searchTerm.toLowerCase();
        const hitsBase = (p.name || "").toLowerCase().includes(searchLower) || (p.mobile || "").includes(searchTerm);

        if (selectedCategory === "motor") {
            const searchClean = searchLower.replace(/-/g, "");
            const vehicleNumberClean = (p.vehicleNumber || "").toLowerCase().replace(/-/g, "");
            return hitsBase || vehicleNumberClean.includes(searchClean);
        }
        return hitsBase;
    });

    const sorted = filtered.sort((a, b) => {
        const dateA = a.createdAt ? new Date(a.createdAt) : new Date(0);
        const dateB = b.createdAt ? new Date(b.createdAt) : new Date(0);
        return dateB - dateA;
    });

    const paginated = sorted.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
    const totalPages = Math.ceil(filtered.length / itemsPerPage);

    // Calculate counts with cross-tab filtering
    const categoryCounts = useMemo(() => {
        const counts = { motor: 0, 'non-motor': 0, life: 0 };
        policies.forEach((p) => {
            if (p.status === "New Lead") return;
            if (selectedCompanies.length > 0 && !selectedCompanies.includes(p.policyCompany)) return;
            if (dateRange.start && dateRange.end) {
                const created = p.createdAt ? (p.createdAt.seconds ? dayjs(p.createdAt.seconds * 1000) : dayjs(p.createdAt)) : null;
                if (!created || !created.isValid()) return;
                const start = dateRange.start.startOf('day');
                const end = dateRange.end.endOf('day');
                if (created.isBefore(start) || created.isAfter(end)) return;
            }
            if (p.policyCategory === 'motor') counts.motor++;
            else if (p.policyCategory === 'non-motor') counts['non-motor']++;
            else if (p.policyCategoryType === 'life') counts.life++;
        });
        return counts;
    }, [policies, selectedCompanies, dateRange]);

    return (
        <Box sx={{ px: 2, pt: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography
                    variant="h5"
                    sx={{
                        fontFamily: 'Roboto Slab',
                        fontWeight: 500,
                        fontSize: isSmallScreen ? '24px' : '32px',
                        fontStyle: 'Medium',
                        lineHeight: isSmallScreen ? '40px' : '56px',
                        letterSpacing: '0.02em',
                        opacity: 1,
                        textEmphasisColor: '#212121',
                    }}
                >
                    Commission
                </Typography>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: isSmallScreen ? 1.5 : 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography
                            sx={{
                                fontFamily: 'Roboto',
                                fontWeight: 400,
                                fontSize: isSmallScreen ? '14px' : '16px',
                                color: '#6B7280',
                            }}
                        >
                            Total Commission:
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', color: '#166534' }}>
                            <CurrencyRupeeIcon sx={{ fontSize: isSmallScreen ? 18 : 20 }} />
                            <Typography
                                sx={{
                                    fontFamily: 'Roboto',
                                    fontWeight: 600,
                                    fontSize: isSmallScreen ? '18px' : '20px',
                                }}
                            >
                                {filtered.reduce((sum, policy) => sum + calculatePolicyCommission(policy), 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </Typography>
                        </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography
                            sx={{
                                fontFamily: 'Roboto',
                                fontWeight: 400,
                                fontSize: isSmallScreen ? '14px' : '16px',
                                color: '#6B7280',
                            }}
                        >
                            Total Premium:
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', color: '#F04438' }}>
                            <CurrencyRupeeIcon sx={{ fontSize: isSmallScreen ? 18 : 20 }} />
                            <Typography
                                sx={{
                                    fontFamily: 'Roboto',
                                    fontWeight: 600,
                                    fontSize: isSmallScreen ? '18px' : '20px',
                                }}
                            >
                                {filtered.reduce((sum, policy) => sum + (parseFloat(policy.netPremium) || 0), 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            </Box>

            <Box
                sx={{
                    width: '100%',
                    background: '#fff',
                    borderRadius: '0',
                    boxShadow: 'none',
                    px: isMobile ? 0.5 : { xs: 2, sm: 3 },
                    pb: isMobile ? 1 : 3,
                    display: 'flex',
                    flexDirection: 'column',
                    position: 'relative',
                    gap: 0,
                    minHeight: 'calc(100vh - 120px)',
                    height: 'calc(100vh - 120px)',
                }}
            >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Tabs
                        value={selectedCategory}
                        onChange={(e, newVal) => {
                            setSelectedCategory(newVal);
                            setCurrentPage(1);
                        }}
                        variant="standard"
                        size="small"
                        indicatorColor="primary"
                        sx={{
                            mb: 1,
                            width: 'fit-content',
                            justifyContent: 'flex-start',
                            '& .MuiTab-root': {
                                textTransform: 'capitalize',
                                fontWeight: 500,
                                minWidth: isSmallScreen ? 60 : 80,
                                px: isSmallScreen ? 1.5 : 2,
                                py: 0.5,
                                fontSize: isSmallScreen ? '13px' : 'inherit',
                                borderRadius: 1,
                                '&:hover': {
                                    backgroundColor: '#E0EBFF',
                                },
                            }
                        }}
                    >
                        {categories.map((cat) => (
                            <Tab
                                key={cat}
                                label={`${cat.charAt(0).toUpperCase() + cat.slice(1)} (${categoryCounts[cat] || 0})`}
                                value={cat}
                                sx={{
                                    '&:hover': {
                                        backgroundColor: '#E0EBFF',
                                    },
                                }}
                            />
                        ))}
                    </Tabs>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        {isAdmin && (
                            <Button
                                variant="outlined"
                                startIcon={<UploadFileIcon />}
                                onClick={handleUploadClick}
                                sx={{
                                    borderRadius: '8px',
                                    px: isSmallScreen ? 1.5 : 2.5,
                                    height: isSmallScreen ? 32 : 40,
                                    textTransform: 'none',
                                    fontWeight: 500,
                                    fontSize: isSmallScreen ? '13px' : 'inherit',
                                    color: '#0063F7',
                                    borderColor: '#E0E0E0',
                                    '&:hover': {
                                        backgroundColor: '#F5F8FF',
                                        borderColor: '#0063F7',
                                        color: '#004DC2'
                                    }
                                }}
                            >
                                Upload PDF
                            </Button>
                        )}
                        <Badge color="primary" badgeContent={activeFilterCount} invisible={activeFilterCount === 0}>
                            <Button
                                variant={activeFilterCount > 0 ? 'contained' : 'outlined'}
                                startIcon={<FilterAltOutlinedIcon />}
                                onClick={handleFilterButtonClick}
                                sx={{
                                    borderRadius: '8px',
                                    px: isSmallScreen ? 1.5 : 2.5,
                                    height: isSmallScreen ? 32 : 40,
                                    textTransform: 'none',
                                    fontWeight: 500,
                                    fontSize: isSmallScreen ? '13px' : 'inherit',
                                    backgroundColor: activeFilterCount > 0 ? '#0063F7' : '#fff',
                                    color: activeFilterCount > 0 ? '#fff' : '#1F2937',
                                    borderColor: '#E0E0E0',
                                    '&:hover': {
                                        color: activeFilterCount > 0 ? '#fff' : '#004DC2',
                                        backgroundColor: activeFilterCount > 0 ? '#0056d6' : '#F5F8FF'
                                    }
                                }}
                            >
                                Filter
                            </Button>
                        </Badge>
                    </Box>
                </Box>
                <Box sx={{ borderBottom: '1px solid #E0E0E0', mb: 2, mt: -1 }} />


                <TextField
                    fullWidth
                    variant="outlined"
                    placeholder="Search client by name or phone number"
                    value={searchTerm}
                    onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setCurrentPage(1);
                    }}
                    size="small"
                    InputProps={{
                        startAdornment: (
                            <Box sx={{ px: 1, display: 'flex', alignItems: 'center' }}>
                                <FiSearch />
                            </Box>
                        )
                    }}
                    sx={{ mb: 0.5 }}
                />

                {loading ? (
                    <Typography>Loading policies...</Typography>
                ) : (
                    <>
                        <TableContainer
                            component={Paper}
                            sx={{
                                boxShadow: 'none',
                                border: 'none',
                                backgroundColor: 'transparent',
                                borderRadius: 0,
                                px: 0,
                                mx: isMobile ? -0.5 : { xs: -2, sm: -3 },
                                width: isMobile ? 'calc(100% + 8px)' : { xs: 'calc(100% + 32px)', sm: 'calc(100% + 48px)' },
                                overflow: 'visible',
                                '& .MuiTableHead-root .MuiTableCell-root': {
                                    borderBottom: '1px solid #E0E0E0',
                                    backgroundColor: 'transparent',
                                },
                                '& .MuiTableBody-root .MuiTableCell-root': {
                                    borderBottom: '1px solid #E0E0E0',
                                    backgroundColor: 'transparent',
                                },
                                '& .MuiTable-root': {
                                    borderLeft: 'none',
                                    borderRight: 'none',
                                },
                                '& .MuiTableRow-root': {
                                    borderRadius: '0 !important',
                                },
                                '& .MuiTableCell-root': {
                                    borderRadius: '0 !important',
                                },
                                '& .MuiPaper-root': {
                                    boxShadow: 'none',
                                    border: 'none',
                                },
                            }}
                        >
                            <Table
                                size="medium"
                                aria-label="commission table"
                                sx={{
                                    border: 'none',
                                    borderCollapse: 'collapse',
                                    width: '100%',
                                    tableLayout: isMobile ? 'auto' : 'fixed',
                                    '& th, & td': {
                                        border: 'none',
                                        px: isMobile ? 0.5 : 0,
                                    },
                                    '& td:first-of-type, & th:first-of-type': {
                                        pl: isMobile ? 1 : 4,
                                    },
                                    '& tr': {
                                        borderBottom: '1px solid #E0E0E0',
                                    },
                                }}
                            >
                                <TableHead>
                                    <TableRow sx={{ borderBottom: '1px solid #E0E0E0' }}>
                                        <TableCell sx={{ width: '10%', px: 0 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', width: 60, height: 24, flexWrap: 'nowrap', whiteSpace: 'nowrap', overflow: 'hidden', textAlign: 'left' }}>
                                                <span style={{
                                                    fontFamily: 'Roboto',
                                                    fontWeight: 500,
                                                    fontSize: '16px',
                                                    lineHeight: '24px',
                                                    letterSpacing: '0.01em',
                                                    color: '#4B5563DE',
                                                    width: 40,
                                                    height: 24,
                                                    opacity: 1,
                                                    whiteSpace: 'nowrap'
                                                }}>#</span>
                                            </Box>
                                        </TableCell>
                                        <TableCell sx={{ width: '18%', px: 0 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                <PersonIcon sx={{ width: 15, height: '13.333px', color: '#0000008A' }} />
                                                <Typography sx={{ fontFamily: 'Roboto', fontWeight: 500, fontSize: '16px', lineHeight: '24px', letterSpacing: '0.01em', color: '#4B5563DE', whiteSpace: 'nowrap' }}>Customer</Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell sx={{ width: '16%', px: 0 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                {selectedCategory === "motor" ? (
                                                    <>
                                                        <DirectionsCarIcon sx={{ width: 15, height: '13.333px', color: '#0000008A' }} />
                                                        <Typography sx={{ fontFamily: 'Roboto', fontWeight: 500, fontSize: '16px', lineHeight: '24px', letterSpacing: '0.01em', color: '#4B5563DE', whiteSpace: 'nowrap' }}>
                                                            Vehicle
                                                        </Typography>
                                                    </>
                                                ) : (
                                                    <>
                                                        <CategoryIcon sx={{ width: 15, height: '13.333px', color: '#0000008A' }} />
                                                        <Typography sx={{ fontFamily: 'Roboto', fontWeight: 500, fontSize: '16px', lineHeight: '24px', letterSpacing: '0.01em', color: '#4B5563DE', whiteSpace: 'nowrap' }}>
                                                            Category
                                                        </Typography>
                                                    </>
                                                )}
                                            </Box>
                                        </TableCell>
                                        <TableCell sx={{ width: '18%', px: 0 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                <DescriptionIcon sx={{ width: 15, height: '13.333px', color: '#0000008A' }} />
                                                <Typography sx={{ fontFamily: 'Roboto', fontWeight: 500, fontSize: '16px', lineHeight: '24px', letterSpacing: '0.01em', color: '#4B5563DE', whiteSpace: 'nowrap' }}>Policy Type</Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell sx={{ width: '14%', px: 0 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                <BusinessIcon sx={{ width: 15, height: '13.333px', color: '#0000008A' }} />
                                                <Typography sx={{ fontFamily: 'Roboto', fontWeight: 500, fontSize: '16px', lineHeight: '24px', letterSpacing: '0.01em', color: '#4B5563DE', whiteSpace: 'nowrap' }}>Company</Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell sx={{ width: '17%', px: 0 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                <PaidIcon sx={{ width: 15, height: '13.333px', color: '#0000008A' }} />
                                                <Typography sx={{ fontFamily: 'Roboto', fontWeight: 500, fontSize: '16px', lineHeight: '24px', letterSpacing: '0.01em', color: '#4B5563DE', whiteSpace: 'nowrap' }}>Net Premium</Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell sx={{ width: '17%', px: 0 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                <CurrencyRupeeIcon sx={{ width: 15, height: '13.333px', color: '#0000008A' }} />
                                                <Typography sx={{ fontFamily: 'Roboto', fontWeight: 500, fontSize: '16px', lineHeight: '24px', letterSpacing: '0.01em', color: '#4B5563DE', whiteSpace: 'nowrap' }}>Commission</Typography>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {paginated.map((row, index) => (
                                        <TableRow
                                            key={row.id || index}
                                            hover
                                            onClick={() => navigate(`/commission/${row.id}`, { state: { policy: row, fromPage: currentPage, fromCategory: selectedCategory } })}
                                            sx={{
                                                height: 48,
                                                verticalAlign: 'middle',
                                                cursor: 'pointer',
                                                '&:hover': { backgroundColor: '#E0EBFF !important' }
                                            }}
                                        >
                                            <TableCell sx={{ px: 0, width: '10%', maxWidth: '10%', verticalAlign: 'middle' }}>
                                                <div style={{
                                                    fontFamily: 'Roboto',
                                                    fontWeight: 400,
                                                    fontSize: '16px',
                                                    lineHeight: '24px',
                                                    letterSpacing: '0.02em',
                                                    color: '#212121',
                                                    height: '48px',
                                                    width: '100%',
                                                    maxWidth: '100%',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                }}>
                                                    {(currentPage - 1) * itemsPerPage + index + 1}
                                                </div>
                                            </TableCell>
                                            <TableCell sx={{ px: 0, width: '18%', maxWidth: '18%', verticalAlign: 'middle', overflow: 'hidden' }}>
                                                <Box>
                                                    <Typography sx={{ fontFamily: 'Roboto', fontWeight: 400, fontSize: '16px', lineHeight: '24px', letterSpacing: '0.02em', color: '#212121', mb: 0.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: '100%', maxWidth: '100%', display: 'block' }}>{row.name || "-"}</Typography>
                                                    <Typography sx={{ fontFamily: 'Roboto', fontWeight: 400, fontSize: '14px', lineHeight: '24px', letterSpacing: '0.01em', color: '#212121', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: '100%' }}>{row.mobile || "-"}</Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell sx={{ px: 0, width: '16%', maxWidth: '16%', verticalAlign: 'middle' }}>
                                                {selectedCategory === "motor" ? (
                                                    <Typography sx={{ fontFamily: 'Roboto', fontWeight: 400, fontSize: '16px', lineHeight: '24px', letterSpacing: '0.02em', color: '#212121', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: '100%' }}>{row.vehicleNumber || "-"}</Typography>
                                                ) : (
                                                    <Typography sx={{ fontFamily: 'Roboto', fontWeight: 400, fontSize: '16px', lineHeight: '24px', letterSpacing: '0.02em', color: '#212121', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: '100%' }}>{row.policyCategoryType || "-"}</Typography>
                                                )}
                                            </TableCell>
                                            <TableCell sx={{ px: 0, width: '18%', maxWidth: '18%', verticalAlign: 'middle' }}>
                                                <Typography sx={{ fontFamily: 'Roboto', fontWeight: 400, fontSize: '16px', lineHeight: '24px', letterSpacing: '0.02em', color: '#212121', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: '100%' }}>{row.policyType || "-"}</Typography>
                                            </TableCell>
                                            <TableCell sx={{ px: 0, width: '15%', maxWidth: '15%', verticalAlign: 'middle' }}>
                                                <Typography sx={{ fontFamily: 'Roboto', fontWeight: 400, fontSize: '16px', lineHeight: '24px', letterSpacing: '0.02em', color: '#212121', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: '100%' }} title={row.policyCompany}>
                                                    {(row.policyCompany && row.policyCompany.length > 20) ? `${row.policyCompany.substring(0, 20)}...` : (row.policyCompany || "-")}
                                                </Typography>
                                            </TableCell>
                                            <TableCell sx={{ px: 0, width: '15%', maxWidth: '15%', verticalAlign: 'middle' }}>
                                                <Typography sx={{ fontFamily: 'Roboto', fontWeight: 400, fontSize: '16px', lineHeight: '24px', letterSpacing: '0.02em', color: '#F04438', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: '100%' }}>₹ {parseFloat(row.netPremium || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Typography>
                                            </TableCell>
                                            <TableCell sx={{ px: 0, width: '15%', maxWidth: '15%', verticalAlign: 'middle' }}>
                                                <Typography sx={{
                                                    fontFamily: 'Roboto',
                                                    fontWeight: 500,
                                                    fontSize: '16px',
                                                    lineHeight: '24px',
                                                    letterSpacing: '0.02em',
                                                    color: calculatePolicyCommission(row) >= 0 ? '#166534' : '#E11D48',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                    width: '100%'
                                                }}>
                                                    ₹ {calculatePolicyCommission(row).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                    {paginated.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                                                <Typography color="textSecondary">No policies found.</Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        {/* Filter Popover */}
                        <Popover
                            open={Boolean(filterAnchorEl)}
                            anchorEl={filterAnchorEl}
                            onClose={handleFilterClose}
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                            slotProps={{ paper: { sx: { p: 2.5, width: 380, maxWidth: '90vw', maxHeight: '80vh', overflowY: 'auto' } } }}
                        >
                            <Stack spacing={2}>
                                {(user?.hasMultipleCompanies !== false) && (
                                    <>
                                        <Box>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <FormControlLabel
                                                    control={<Checkbox size="small" checked={showCompanyOptions} onChange={(e) => handleCompanyCheckboxChange(e.target.checked)} />}
                                                    label="Company"
                                                    sx={{ mb: 0 }}
                                                />
                                            </Box>
                                            {showCompanyOptions && (
                                                <Box sx={{ pl: 3, pr: 1, maxHeight: 200, overflowY: 'auto' }}>
                                                    {(() => {
                                                        const companies = selectedCategory === 'motor' ? COMPANIES.motor : selectedCategory === 'life' ? COMPANIES.life : COMPANIES.non_motor;
                                                        return companies.map((company) => (
                                                            <FormControlLabel
                                                                key={company}
                                                                control={<Checkbox size="small" checked={selectedCompanies.includes(company)} onChange={() => handleCompanyToggle(company)} />}
                                                                label={company}
                                                                sx={{ mb: 0.5, display: 'block' }}
                                                            />
                                                        ));
                                                    })()}
                                                </Box>
                                            )}
                                        </Box>
                                        <Divider />
                                    </>
                                )}
                                <Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <FormControlLabel
                                            control={<Checkbox size="small" checked={showPolicyTypeOptions} onChange={(e) => handlePolicyTypeCheckboxChange(e.target.checked)} />}
                                            label="Policy Type"
                                            sx={{ mb: 0 }}
                                        />
                                    </Box>
                                    {showPolicyTypeOptions && (
                                        <Box sx={{ pl: 3, pr: 1, maxHeight: 200, overflowY: 'auto' }}>
                                            {(() => {
                                                const policyTypes = selectedCategory === 'motor' ? POLICY_TYPES.motor : selectedCategory === 'life' ? POLICY_TYPES.life : POLICY_TYPES.health;
                                                return policyTypes.map((policyType) => (
                                                    <FormControlLabel
                                                        key={policyType}
                                                        control={<Checkbox size="small" checked={(selectedPolicyTypes[selectedCategory] || []).includes(policyType)} onChange={() => handlePolicyTypeToggle(policyType)} />}
                                                        label={policyType}
                                                        sx={{ mb: 0.5, display: 'block' }}
                                                    />
                                                ));
                                            })()}
                                        </Box>
                                    )}
                                </Box>
                                <Divider />
                                <Box>
                                    <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block', fontWeight: 500 }}>Date Range (Created)</Typography>
                                    <Stack direction="row" spacing={1.5} sx={{ mb: 1.5 }}>
                                        <Button variant={showDateRangeCalendar === 'start' ? 'contained' : 'outlined'} size="small" onClick={() => setShowDateRangeCalendar(showDateRangeCalendar === 'start' ? null : 'start')} sx={{ flex: 1, textTransform: 'none', '&:hover': { backgroundColor: showDateRangeCalendar === 'start' ? '#0056d6' : '#E0EBFF', borderColor: '#E0EBFF' } }}>
                                            {dateRange.start ? dateRange.start.format('DD MMM') : 'Start Date'}
                                        </Button>
                                        <Button variant={showDateRangeCalendar === 'end' ? 'contained' : 'outlined'} size="small" onClick={() => setShowDateRangeCalendar(showDateRangeCalendar === 'end' ? null : 'end')} sx={{ flex: 1, textTransform: 'none', '&:hover': { backgroundColor: showDateRangeCalendar === 'end' ? '#0056d6' : '#E0EBFF', borderColor: '#E0EBFF' } }} disabled={!Boolean(dateRange.start)}>
                                            {dateRange.end ? dateRange.end.format('DD MMM') : 'End Date'}
                                        </Button>
                                    </Stack>
                                    {showDateRangeCalendar && (
                                        <Box
                                            ref={calendarRef}
                                            sx={{
                                                position: 'relative',
                                                zIndex: 1300,
                                                boxShadow: 3,
                                                borderRadius: 2,
                                                bgcolor: 'background.paper',
                                                width: '100%',
                                                overflow: 'hidden',
                                                padding: '12px',
                                                mb: 1.5,
                                                minHeight: '320px',
                                                maxHeight: '320px',
                                                '& .react-calendar': {
                                                    width: '100%',
                                                    maxWidth: '100%',
                                                    minHeight: '280px',
                                                    '__navigation': {
                                                        display: 'flex',
                                                        justifyContent: 'space-between',
                                                        alignItems: 'center',
                                                        padding: '0 6px',
                                                        margin: '0 0 8px 0',
                                                        '__label': {
                                                            minWidth: '180px',
                                                            textAlign: 'center',
                                                            flexGrow: 0,
                                                            fontWeight: '600 !important',
                                                            margin: 0,
                                                            padding: '8px 0',
                                                            fontSize: '16px',
                                                            lineHeight: '1.4'
                                                        },
                                                        '__arrow': {
                                                            minWidth: '32px',
                                                            padding: '8px 2px',
                                                            margin: 0
                                                        }
                                                    },
                                                    '__month-view__weekdays': {
                                                        display: 'flex',
                                                        justifyContent: 'space-between',
                                                        padding: '0 6px',
                                                        marginBottom: '4px',
                                                        '__weekday': {
                                                            width: '36px',
                                                            padding: '4px 0',
                                                            textAlign: 'center',
                                                            fontSize: '12px',
                                                            fontWeight: '600 !important',
                                                            textTransform: 'uppercase',
                                                            color: '#666 !important',
                                                            backgroundColor: 'transparent !important',
                                                            border: 'none !important',
                                                            '& abbr': {
                                                                textDecoration: 'none',
                                                                display: 'inline-block',
                                                                width: '100%',
                                                                textAlign: 'center'
                                                            }
                                                        }
                                                    },
                                                    '__month-view__days': {
                                                        display: 'grid !important',
                                                        gridTemplateColumns: 'repeat(7, 1fr)',
                                                        gap: '4px',
                                                        padding: '0 6px',
                                                        '__day': {
                                                            width: '36px',
                                                            height: '36px',
                                                            margin: '0 auto',
                                                            fontWeight: '600 !important',
                                                            padding: '0',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            fontSize: '13px',
                                                            '__neighboringMonth': {
                                                                opacity: 0.5
                                                            },
                                                            '__weekend': {
                                                                color: '#212121 !important'
                                                            },
                                                            '__now': {
                                                                fontWeight: 'bold',
                                                                color: '#004DC2 !important',
                                                                backgroundColor: 'transparent !important',
                                                                '&:enabled:hover': {
                                                                    backgroundColor: '#E0EBFF !important'
                                                                }
                                                            }
                                                        }
                                                    }
                                                },
                                                '& .react-calendar__tile': {
                                                    fontFamily: 'Roboto',
                                                    fontWeight: 500,
                                                    fontSize: '13px',
                                                    color: '#212121 !important',
                                                    transition: 'all 0.2s ease',
                                                    '&:enabled:hover': {
                                                        backgroundColor: '#E0EBFF !important',
                                                        color: '#212121 !important'
                                                    },
                                                    '&--active, &--active:enabled, &--active:enabled:hover': {
                                                        backgroundColor: '#004DC2 !important',
                                                        color: '#fff !important',
                                                        borderRadius: '50%',
                                                        border: 'none',
                                                        boxShadow: 'none',
                                                        outline: 'none'
                                                    }
                                                },
                                                "& .react-calendar__tile--active, & .react-calendar__tile--active:enabled": {
                                                    backgroundColor: '#004DC2 !important',
                                                    color: '#fff !important',
                                                },
                                                "& .react-calendar__month-view__days__day--weekend": {
                                                    color: '#212121 !important',
                                                },
                                                "& .react-calendar__tile:not(.react-calendar__tile--active):enabled:hover": {
                                                    backgroundColor: '#E0EBFF !important',
                                                },
                                                "& .react-calendar__navigation button": {
                                                    color: '#333 !important',
                                                    background: 'none !important',
                                                    fontWeight: '600 !important',
                                                    border: 'none !important',
                                                    outline: 'none !important',
                                                    '&:hover': {
                                                        backgroundColor: '#E0EBFF !important',
                                                        color: '#333 !important',
                                                    },
                                                    '&:focus': {
                                                        backgroundColor: 'transparent !important',
                                                        outline: 'none !important',
                                                        boxShadow: 'none !important',
                                                    },
                                                    '&:active': {
                                                        backgroundColor: 'transparent !important',
                                                    },
                                                },
                                                "& .react-calendar__navigation__label": {
                                                    color: '#333 !important',
                                                    background: 'transparent !important',
                                                    fontWeight: '600 !important',
                                                    '&:hover': {
                                                        backgroundColor: 'transparent !important',
                                                    },
                                                    '&:focus': {
                                                        backgroundColor: 'transparent !important',
                                                        outline: 'none !important',
                                                    },
                                                },
                                            }}
                                        >
                                            <Calendar value={showDateRangeCalendar === 'start' ? dateRange.start?.toDate() : dateRange.end?.toDate()} onChange={handleDateRangeCalendarChange} calendarType="gregory" locale="en" maxDetail="month" minDetail="month" />
                                        </Box>
                                    )}
                                </Box>
                                <Divider />
                                <Stack direction="row" spacing={1.5} justifyContent="flex-end">
                                    <Button onClick={clearFilters} size="small" color="inherit" sx={{ '&:hover': { backgroundColor: '#F5F5F5' } }}>Clear filters</Button>
                                    <Button onClick={handleFilterClose} size="small" variant="contained">Done</Button>
                                </Stack>
                            </Stack>
                        </Popover>

                        {/* Spacer to push pagination to bottom */}
                        <Box sx={{ flexGrow: 1 }} />

                        {/* Pagination moved below */}
                        {totalPages > 1 && (
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'flex-end',
                                    mt: 'auto', // Push pagination to bottom
                                    pt: 1,
                                    pb: 1,
                                    backgroundColor: '#fff',
                                    borderRadius: '0',
                                    boxShadow: 'none'
                                }}
                            >
                                <Pagination
                                    count={Math.max(totalPages, 1)}
                                    page={currentPage}
                                    onChange={(_, value) => setCurrentPage(value)}
                                    sx={{
                                        '& .MuiPaginationItem-root': {
                                            borderRadius: '50%',
                                            border: '1px solid #D1D5DB',
                                            width: 36,
                                            height: 36,
                                            margin: '0 4px',
                                            color: '#6B7280',
                                            '&:hover': {
                                                backgroundColor: '#E0EBFF',
                                            },
                                        },
                                        '& .MuiPaginationItem-previousNext': {
                                            borderRadius: 0,
                                            border: 'none',
                                            backgroundColor: 'transparent',
                                            width: 'auto',
                                            height: 'auto',
                                            margin: '0 4px',
                                            color: '#6B7280',
                                            '&:hover': {
                                                backgroundColor: 'transparent',
                                            },
                                        },
                                        '& .Mui-selected': {
                                            backgroundColor: '#004DC2 !important',
                                            color: '#fff !important',
                                            border: 'none',
                                            '&:hover': {
                                                backgroundColor: '#004DC2 !important',
                                            },
                                        },
                                    }}
                                />
                            </Box>
                        )}
                    </>
                )}
            </Box>

            {/* Upload Dialog */}
            {/* Upload Dialog */}
            <Dialog
                open={uploadDialogOpen}
                onClose={handleUploadClose}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: { borderRadius: 2 } }}
            >
                <DialogTitle sx={{ m: 0, p: 2, textAlign: "left !important", position: 'relative' }}>
                    <Typography
                        sx={{
                            fontFamily: "Roboto Slab !important",
                            fontWeight: "500 !important",
                            fontSize: "32px !important",
                            lineHeight: "56px !important",
                            letterSpacing: "0.02em !important",
                            color: "#000000 !important",
                            textAlign: "left !important"
                        }}
                    >
                        {uploadResult ? "Update Result" : "Upload Commission File"}
                    </Typography>
                    <IconButton
                        aria-label="Close"
                        onClick={handleUploadClose}
                        sx={{ position: 'absolute', right: 8, top: 16 }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ textAlign: "center", pt: 1 }}>
                    {!uploadResult && !uploadLoading && (
                        <Box sx={{ py: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <FormControl fullWidth size="small">
                                <InputLabel id="upload-company-label">Select Company</InputLabel>
                                <Select
                                    labelId="upload-company-label"
                                    value={uploadCompany}
                                    label="Select Company"
                                    onChange={(e) => {
                                        setUploadCompany(e.target.value);
                                        setUploadFile(null); // Clear file when company changes
                                        setUploadError(null);
                                    }}
                                >
                                    <MenuItem value="NEW_INDIA">New India Assurance (PDF)</MenuItem>
                                    <MenuItem value="LIC">LIC (PDF)</MenuItem>
                                    <MenuItem value="STAR">Star (PDF)</MenuItem>
                                    <MenuItem value="GODIGIT">GoDigit (Excel)</MenuItem>
                                </Select>
                            </FormControl>

                            <Box
                                onDragOver={handleDragOver}
                                onDragLeave={handleDragLeave}
                                onDrop={handleDrop}
                                sx={{
                                    border: isDragging ? "2px dashed #0063F7" : "2px dashed #ccc",
                                    borderRadius: "4px",
                                    p: 4,
                                    backgroundColor: isDragging ? "#F0F7FF" : "#FAFAFA",
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    minHeight: 180,
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease-in-out',
                                    '&:hover': { backgroundColor: '#F0F5FF', borderColor: '#0063F7' }
                                }}
                                onClick={() => fileInputRef.current?.click()}
                            >
                                <PictureAsPdfIcon sx={{ fontSize: 40, color: "#757575", mb: 1 }} />
                                <Typography
                                    sx={{
                                        fontFamily: "Roboto",
                                        fontWeight: 400,
                                        fontSize: "22px",
                                        lineHeight: "30px",
                                        mb: 1,
                                        textAlign: 'center'
                                    }}
                                >
                                    Drag and drop or click to upload. We'll extract the data for you
                                </Typography>
                                <Button
                                    sx={{
                                        color: "#0063F7",
                                        textTransform: "none",
                                        fontSize: "20px",
                                        "&:hover": { textDecoration: "underline", backgroundColor: "transparent" }
                                    }}
                                >
                                    Choose files
                                </Button>
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept={uploadCompany === 'GODIGIT' ? '.xlsx,.xls' : '.pdf'}
                                    onChange={handleFileSelect}
                                    style={{ display: 'none' }}
                                />
                            </Box>
                            {uploadFile && (
                                <Typography sx={{ fontSize: "14px", color: "#666", textAlign: "center" }}>
                                    Selected: {uploadFile.name}
                                </Typography>
                            )}
                            {uploadError && (
                                <Alert severity="error" sx={{ mt: 1 }}>{uploadError}</Alert>
                            )}
                        </Box>
                    )}

                    {uploadLoading && (
                        <Box sx={{ py: 6, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
                            <Typography variant="h6">Extracting Commission Data...</Typography>
                            <Box sx={{ width: '100%', px: 4 }}>
                                <LinearProgress sx={{ height: 10, borderRadius: 5, backgroundColor: "#E5E7EB", "& .MuiLinearProgress-bar": { backgroundColor: "#0063F7" } }} />
                            </Box>
                            <Typography variant="body2" color="text.secondary">This may take a few moments</Typography>
                        </Box>
                    )}

                    {uploadResult && (
                        <Box sx={{ py: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                            <CheckCircleIcon sx={{ fontSize: 64, color: '#166534', mb: 1 }} />
                            <Typography
                                variant="h5"
                                sx={{
                                    fontFamily: 'Roboto Slab',
                                    fontWeight: 500,
                                    color: '#166534',
                                    mb: 1
                                }}
                            >
                                Hurray! Your commissions have been updated
                            </Typography>

                            <Paper variant="outlined" sx={{ p: 3, width: '100%', backgroundColor: '#F0FDF4', borderColor: '#BBF7D0' }}>
                                <Stack spacing={1} sx={{ textAlign: 'left' }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography fontWeight={500}>Total Extracted:</Typography>
                                        <Typography fontWeight={600}>{uploadResult.totalExtracted || 0}</Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography fontWeight={500} color="#166534">Successfully Updated:</Typography>
                                        <Typography fontWeight={600} color="#166534">{uploadResult.successfullyUpdated || 0}</Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography fontWeight={500} color="#92400E">Skipped (No matched Policy):</Typography>
                                        <Typography fontWeight={600} color="#92400E">{uploadResult.skipped?.length || 0}</Typography>
                                    </Box>
                                </Stack>
                            </Paper>
                        </Box>
                    )}
                </DialogContent>
                {!uploadLoading && (
                    <DialogActions sx={{ p: 3, gap: 2, justifyContent: 'flex-end' }}>
                        {!uploadResult ? (
                            <>
                                <Button
                                    onClick={handleUploadClose}
                                    sx={{
                                        width: 149,
                                        height: 60,
                                        borderRadius: '6px',
                                        borderColor: "#B00020",
                                        color: "#B00020",
                                        fontSize: '20px',
                                        textTransform: 'none',
                                        '&:hover': { borderColor: "#B00020", backgroundColor: '#FBEDED' }
                                    }}
                                    variant="outlined"
                                >
                                    Cancel
                                </Button>
                                <Button
                                    onClick={handleUploadSubmit}
                                    disabled={!uploadFile}
                                    variant="contained"
                                    sx={{
                                        width: 149,
                                        height: 60,
                                        borderRadius: '6px',
                                        backgroundColor: '#0063F7 !important',
                                        fontSize: '20px',
                                        textTransform: 'none',
                                        '&:hover': { backgroundColor: '#0056D6 !important' },
                                        '&:disabled': { backgroundColor: '#E0E0E0 !important', color: '#9E9E9E !important' }
                                    }}
                                >
                                    Submit
                                </Button>
                            </>
                        ) : (
                            <>
                                <Button
                                    onClick={handleUploadClose}
                                    variant="outlined"
                                    sx={{
                                        width: 149,
                                        height: 60,
                                        borderRadius: '6px',
                                        borderColor: "#B00020",
                                        color: "#B00020",
                                        fontSize: '20px',
                                        textTransform: 'none',
                                        '&:hover': { borderColor: "#B00020", backgroundColor: '#FBEDED' }
                                    }}
                                >
                                    Close
                                </Button>
                                <Button
                                    onClick={handleUploadClose}
                                    variant="contained"
                                    sx={{
                                        width: 149,
                                        height: 60,
                                        borderRadius: '6px',
                                        backgroundColor: '#0063F7 !important',
                                        fontSize: '20px',
                                        textTransform: 'none',
                                        '&:hover': { backgroundColor: '#0056D6 !important' }
                                    }}
                                >
                                    Done
                                </Button>
                            </>
                        )}
                    </DialogActions>
                )}
            </Dialog>
        </Box>
    );
};

export default CommissionList;
